import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { API_BASE_URL } from '../auth';
import { useLanguage } from '../contexts/LanguageContext';

interface GuestInfo {
  report_id: string;
  project_description: string;
  cargo_weight_mt: number;
  created_at: string;
  photo_urls?: string[];
}

// Use global API_BASE_URL
const API_URL = API_BASE_URL;

export const GuestSurveyUpload: React.FC = () => {
  const { guest_token } = useParams<{ guest_token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [guestInfo, setGuestInfo] = useState<GuestInfo | null>(null);
  const { t } = useLanguage();

  // Form State
  const [implementationDate, setImplementationDate] = useState(new Date().toISOString().split('T')[0]);
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  
  // Photos State: array of 12 elements (File objects or null)
  const [photos, setPhotos] = useState<(File | null)[]>(Array(12).fill(null));
  const [previews, setPreviews] = useState<(string | null)[]>(Array(12).fill(null));
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const photoCategories = [
    t('surveys.guest.categories.floor'),
    t('surveys.guest.categories.cargo'),
    t('surveys.guest.categories.front_right'),
    t('surveys.guest.categories.front_left'),
    t('surveys.guest.categories.rear_right'),
    t('surveys.guest.categories.rear_left'),
    t('surveys.guest.categories.plate'),
    t('surveys.guest.categories.seal'),
    t('surveys.guest.categories.overall'),
    t('surveys.guest.categories.other_1'),
    t('surveys.guest.categories.other_2'),
    t('surveys.guest.categories.other_3')
  ];

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/surveys/guest/${guest_token}`);
        if (!response.ok) throw new Error(t('surveys.guest.invalid_link'));
        const data = await response.json();
        setGuestInfo(data);
        
        if (data.photo_urls && data.photo_urls.length > 0) {
          const newPreviews = [...previews];
          data.photo_urls.forEach((url: string, index: number) => {
            if (index < 12) newPreviews[index] = url;
          });
          setPreviews(newPreviews);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (guest_token) fetchInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest_token, t]);

  const handlePhotoSelect = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // 1. Client-Side Browser Image Compression (< 2MB)
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // 2. Set file logic
      const newPhotos = [...photos];
      newPhotos[index] = compressedFile;
      setPhotos(newPhotos);

      // 3. Set preview logic
      const previewUrl = URL.createObjectURL(compressedFile);
      const newPreviews = [...previews];
      newPreviews[index] = previewUrl;
      setPreviews(newPreviews);
      
    } catch (error) {
      console.error(t('surveys.guest.compression_error'), error);
      alert(t('surveys.guest.compression_error'));
    }
  };

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos[index] = null;
    setPhotos(newPhotos);
    
    const newPreviews = [...previews];
    newPreviews[index] = null;
    setPreviews(newPreviews);
    
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index]!.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validPhotos = photos.filter(p => p !== null);
    const existingCount = guestInfo?.photo_urls?.length || 0;
    
    if (validPhotos.length === 0 && existingCount === 0) {
      alert(t('surveys.guest.min_photo_error'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      if (implementationDate) formData.append('implementation_date', implementationDate);
      if (company) formData.append('implementation_company', company);
      if (name) formData.append('implementer_name', name);
      if (phone) formData.append('implementer_phone', phone);
      if (email) formData.append('implementer_email', email);
      
      // Ensure photos array sends matching the list properly
      photos.forEach((file, index) => {
          if (file) {
              formData.append('photos', file, `photo_${index}.jpg`);
          }
      });

      const response = await fetch(`${API_URL}/api/surveys/upload/${guest_token}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Error during upload.');
      }

      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500 font-medium">{t('surveys.modal.loading')}</div>;
  if (error) return <div className="p-8 text-center text-red-600 bg-red-50 m-4 rounded border border-red-100">{error}</div>;
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md text-center border border-slate-100">
          <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">{t('surveys.guest.upload_success_title')}</h2>
          <p className="text-slate-600 mb-8 leading-relaxed">{t('surveys.guest.upload_success_desc')}</p>
          <button onClick={() => window.close()} className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md">
            {t('surveys.guest.close_window')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-600 px-8 py-10 text-white text-center">
          <h1 className="text-3xl font-bold mb-3 tracking-tight">{t('surveys.guest.title')}</h1>
          {guestInfo && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 inline-block text-left text-sm border border-white/20">
              <p><span className="opacity-70 font-medium">Rapor ID:</span> <span className="font-mono">{guestInfo.report_id}</span></p>
              <p><span className="opacity-70 font-medium">{t('surveys.portal.editor.project_name')}:</span> <span className="font-semibold">{guestInfo.project_description}</span></p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          {/* Metadata Section */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-slate-800 border-b border-slate-100 pb-3 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center text-sm">1</span>
              {t('surveys.guest.info_title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('surveys.guest.date')}</label>
                <input type="date" value={implementationDate} onChange={e => setImplementationDate(e.target.value)} className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all p-2.5 border outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('surveys.guest.company_name')}</label>
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder={t('surveys.guest.company_placeholder')} className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all p-2.5 border outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('surveys.guest.implementer_name')}</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all p-2.5 border outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('surveys.guest.phone')}</label>
                <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+90 ..." className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all p-2.5 border outline-none font-mono" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('surveys.guest.email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('surveys.guest.email_placeholder')} className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all p-2.5 border outline-none" />
              </div>
            </div>
          </div>

          {/* Photo Grid Section */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-slate-800 border-b border-slate-100 pb-3 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center text-sm">2</span>
              {t('surveys.guest.photos_title')}
            </h3>
            <p className="text-sm text-slate-500 mb-8 leading-relaxed bg-slate-50 p-4 rounded-lg border border-slate-100 italic">
              {t('surveys.guest.photos_desc')}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {photoCategories.map((title, index) => (
                <div key={index} className="group border-2 border-dashed border-slate-200 rounded-xl p-3 flex flex-col items-center justify-center relative bg-white hover:bg-slate-50 hover:border-indigo-300 transition-all duration-200 overflow-hidden h-48">
                  
                  {previews[index] ? (
                    <div className="relative w-full h-full">
                      <img src={previews[index]!} alt={title} className="w-full h-full object-cover rounded-lg" />
                      <button type="button" onClick={() => removePhoto(index)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-10 border-2 border-white">✖</button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-2 text-center truncate backdrop-blur-sm rounded-b-lg font-medium">{title}</div>
                    </div>
                  ) : (
                    <div className="text-center cursor-pointer w-full h-full flex flex-col items-center justify-center px-2" onClick={() => fileInputRefs.current[index]?.click()}>
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-indigo-100 group-hover:scale-110 transition-all">
                        <svg className="h-6 w-6 text-slate-400 group-hover:text-indigo-500" stroke="currentColor" fill="none" viewBox="0 0 48 48"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </div>
                      <span className="block text-xs font-bold text-slate-700 mb-1 group-hover:text-indigo-600">{title}</span>
                      <span className="block text-[10px] text-indigo-500 font-bold uppercase tracking-wider">{t('surveys.guest.take_photo')}</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    ref={el => { fileInputRefs.current[index] = el; }}
                    onChange={(e) => handlePhotoSelect(index, e)}
                    className="hidden"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-xl text-white font-extrabold text-lg shadow-lg transform transition-all active:scale-95 ${isSubmitting ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200'}`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  {t('surveys.guest.uploading')}
                </span>
              ) : t('surveys.guest.confirm_upload')}
            </button>
            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-[0.2em]">Lashing Plan Engine &copy; 2026</p>
          </div>
        </form>
      </div>
    </div>
  );
};
